<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { $bus, mqtt } from '@/common/events'
import { dialog } from '@/common/dialog'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import Pagination from '@/components/Pagination.vue'
import DropDown from '@/components/DropDown.vue'
import Toast from '@/components/Toast.vue'
import Label from '@/components/Label.vue'
import TextBox from '@/components/TextBox.vue'
import Button from '@/components/Button.vue'
import EventLog from '@/components/EventLog.vue'
import Fullscreen from '@/components/Fullscreen.vue'
import AutocompleteTextBox from '@/components/AutocompleteTextBox.vue'
import InsertIndicator from '@/components/InsertIndicator.vue'
import KeyboardNumpad from '@/components/KeyboardNumpad.vue'
import { useTranslate } from '@/composables/language'
import { M280CabinetControl } from '@/drivers/cabinet/PLT-M280.js'
import { CabinetService } from "@/services/CabinetService"

let cabinet = null;

let cabinetService = new CabinetService();

const props = defineProps({
  cabinet: {
    type: Object,
    default: null
  },
  user: {
    type: Object,
    default: null
  },
});

const emit = defineEmits([ "onLog" ]);
function insertLog(record) {
  emit("onLog", record);
}

const { t } = useTranslate();

const mac = computed(() => {
  return props.cabinet?.mac;
});

const ip = computed(() => {
  if (props.cabinet) {
    let type = props.cabinet.protocol || props.cabinet.type || "";
    let host = props.cabinet.host;
    let port = props.cabinet.port;
    return `${type} ${host}:${port}`.trim();
  }
  return null;
});

watch(mac, (newValue) => {
  initializeController();
});

watch(props.cabinet, (newValue) => {
  //console.log("watch cabinet", newValue)
  //isReferenced.value = newValue?.isReferenced === true;
  //isReferencing.value = newValue?.isReferencing === true;
});

const instance = getCurrentInstance();

const isModalVisible = ref(false);
const isOpening = ref(false);
const trayIndex = ref(1);
const trayNumber = ref();

let lastCommandRaw = null;
let lastCommandTime = null;
let insideRecurring = false;

const categories = ref([
  { name: "A" },
  { name: "B" },
  { name: "C" },
  { name: "D" },
  { name: "E" },
]);

const sortOptions = ref([
  { name: "Po abecedi" },
  { name: "Po zasedenosti" }, //Kapaciteta
]);

const suggestions = ref([
  { name: "A/0015" },
  { name: "B/0015" },
  { name: "C/0015" },
]);

const isReferenced = ref(null);
const movementStatus = ref(null);
const internalState = ref(null);
const motorStatus = ref(null);
//const motors = ref([ 'A', 'B', 'X', 'Y', 'Z', 'P' ]);
const motors = ref([ 'A', 'B', 'P', 'X', 'Y', 'Z' ]);

const isHaltAlarm = ref(null);
const isCurtainAlarm = ref(null);
const isDisruptAlarm = ref(null);

async function initializeController() {
  if (mac.value) {
    if (cabinet) {
      cabinet.dispose();
    }

    cabinet = new M280CabinetControl({ mqtt: mqtt, mac: mac.value });

    //Get status when change occurs
    cabinet.on("status", async (data) => {
      //console.log("M280 status", data);

      if (typeof data.referenced != "undefined") {
        isReferenced.value = data.referenced;
      }
      if (typeof data.internalState != "undefined") {
        internalState.value = data.internalState;
      }
      if (typeof data.motorStatus != "undefined") {
        motorStatus.value = data.motorStatus;
      }
      if (typeof data.movementStatus != "undefined") {
        movementStatus.value = data.movementStatus;
      }
      if (data.status == "error" && data.substatus == "alarm_curtain") {
        isCurtainAlarm.value = true;
      }
      if (data.status == "error" && data.substatus == "alarm_halt") {
        isHaltAlarm.value = true;
      }
      if (data.status == "error" && data.substatus == "disrupt") {
        isDisruptAlarm.value = true;
      }
      //applyStatus(data);
    });

    //Get last status now
    //applyStatus(await hcu.cabinet(mac.value).getStatus2());
  }
}


let isTrayOpen = null;
function openChanged(newValue) {
  if (newValue == true) {
    isOpening.value = false;
  }
  //alert("Tray changed: " + newValue);

  if (newValue == true) {
    dialog({
      type: "success",
      //title: "Potrditev",
      text: t('service.tray_opened'),
      confirmButtonText: t('generic.ok'),
      //cancelButtonText: "Prekliči",
    });
  }
}

const eventLogElement = ref();
const eventLogHeight = ref(300);

function calculateEventLogHeight() {
  //Calculate event log height
  let element = document.getElementById("eventLog");
  let offset = element.getBoundingClientRect(); //Get element position within the window
  eventLogHeight.value = window.innerHeight - offset.top - 20;
}

onMounted(async () => {
  reset();

  initMQTT();

  await initializeController();
  calculateEventLogHeight();
});

onUnmounted(() => {
  if (cabinet) {
    cabinet.dispose();
  }
});

function effectiveUnits(inserts) {
  let list = [];
  let parts = inserts.split(/,/g);
  let count = 0;
  for (let i = 0; i < parts.length; i++) {
    let part = parts[i];
    let len = parseInt(part.replace(/[^\d]+/g, ""));
    let empty = part.includes("?");
    if (!empty) ++count;
  }
  return count;
}

function highlightInsert(inserts, index) {
  let list = [];
  let result = "";
  let parts = inserts.split(/,/g);
  let count = 0;
  for (let i = 0; i < parts.length; i++) {
    let part = parts[i];
    let len = parseInt(part.replace(/[^\d]+/g, ""));
    let empty = part.includes("?");
    if (!empty) ++count;
    let unit = !empty ? (index == count ? len : (len + "x")) : (len + "?");
    list.push(unit);
  }
  result = list.join(",");
  return result;
}

function showModal(i) {
  trayIndex.value = i;
  isModalVisible.value = true;
  isOpening.value = false;
}

function closeModal() {
  isModalVisible.value = false;
}

function getArguments() {
  let number = trayNumber.value;
  let curtainStart = selectedCurtainStart.value;
  let curtainStop = selectedCurtainStop.value;

  //Check if selected in reverse order from right to left
  if (curtainStop < curtainStart) {
    curtainStop = selectedCurtainStart.value; //Reverse
    curtainStart = selectedCurtainStop.value; //Reverse
  }

  //Keep values inside the bounds
  if (!curtainStart) {
    curtainStart = 1;
    curtainStop = 10;
  }
  else if (!curtainStop || curtainStart > curtainStop) {
    curtainStop = 10;
  }

  //Calculate curtain opening length
  let curtainLength = curtainStop - curtainStart + 1;

  let result = {
    trayNumber: number,
    curtainStart,
    curtainLength,
    outputWindow: 1
  };

  return result;
}

async function openTray(options) {
  lastAction.value = "deliver";

  try {
    if (!trayNumber.value) {
      throw new Error(t('service.enter_tray_number'));
    }

    let args = getArguments();

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    //logRecords.value.push({ text: `${doorNumber} ${drumPosition}`, type: "command", direction: "outbound", time: time });
    //let data = await cabinetService.openDrum({ drumPosition, doorNumber });

    if (options?.serviceWindow) {
      args.serviceWindow = options?.serviceWindow;
    }

    try {
      let trayName = "" + trayNumber.value;
      //args.serviceWindow
      insertLog({
        uid: props.user?.id,
        tn: trayName,
        cid: props.cabinet?.id,
        msg: `Servisno okno: odpiranje predala ${trayName}, pozicija zavese ${args.curtainStart}, dolžina odprtine ${args.curtainLength}` + (props.cabinet ? " na omari " + (props.cabinet?.name || "???") : ""),
        ts: new Date()
      });
    }
    catch (err) {
    }

    //socket.emit("publish", { topic: "hcu/cabinet/drum/open", payload:{ drumPosition, doorNumber } });
    //mqtt.publish({ topic: "hcu/cabinets/" + mac.value + "/open", payload: { drumPosition, doorNumber } });
    await cabinet.openTray(args, options);
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);
  }
}

async function closeTray() {
  lastAction.value = "store";

  try {
    const withArgs = false;
    let args = withArgs ? getArguments() : null;

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;

    try {
      let trayName = "" + trayNumber.value;
      insertLog({
        uid: props.user?.id,
        tn: trayName,
        cid: props.cabinet?.id,
        msg: `Servisno okno: zapiranje zadnjega odprtega predala ${trayName}` + (props.cabinet ? " na omari " + (props.cabinet?.name || "???") : ""),
        ts: new Date()
      });
    }
    catch (err) {
    }
    
    await cabinet.closeTray(args);
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);
  }
}

async function goToRef() {
  try {
    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    
    await cabinet.goToRef();
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);
  }
}

async function set800() {
  try {
    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    
    await cabinet.requestClearEEPROM();
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);
  }
}

async function set903() {
  try {
    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);
    lastCommandTime = now;
    
    appendLog({ text: "SET 903", type: "command", direction: "inbound" });
    await cabinet.invokeCommand("SET 903");
  }
  catch (err) {
    isOpening.value = false;
    appendLog({ text: err.message, type: "danger", direction: "outbound" });
    console.error(err);
  }
}

async function promptSet800() {
  let result = await dialog({
    type: "question-danger",
    title: t('service.confirm_title'),
    text: t('service.clear_eeprom_text'),
    confirmButtonText: t('generic.clear'),
    cancelButtonText: t('generic.cancel'),
  });

  if (result && result.confirm) {
    try {
      await set800();
    }
    catch (err) {
      console.error(err);
      dialog(err);
    }
  }
}

function submit() {
  if (lastAction.value == "deliver") {
    closeTray(true);
  }
  else {
    openTray();
  }
}

const logRecords = ref([
  /*
  { text: "90,4-7", time: "13:23:00", type: "command", direction: "outbound" },
  { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+12ms" },
  { text: "DONE", time: "13:23:07", type: "success", direction: "inbound", elapsed: "+7245ms" },
  { text: "170,1-10", time: "13:23:10", type: "command", direction: "outbound" },
  { text: "ERR", time: "13:23:10", type: "danger", direction: "inbound" },
  */
]);

const lastAction = ref(null);

const currentTrayNumber = ref(null);
const currentCurtainPosition = ref(null);

const selectedTrayNumber = ref(null);
const selectedCurtainPosition = ref(null);
const selectedRow = ref(2);
const selectedColumn = ref(3);
const selectedCurtainStart = ref(null);
const selectedCurtainStop = ref(null);
let isSelectingCurtainStart = true;

function start() {
  let drum = selectedDrumPosition.value;
  let door = selectedDoorNumber.value;
  openDrum(drum, door);
}

function reset() {
  isOpening.value = false;
  //currentDrumPosition.value = null;
  //currentDoorNumber.value = null;
  //selectedDrumPosition.value = null;
  //selectedDoorNumber.value = null;
  logRecords.value = [ ];
}

function selectRow(i) {
  selectedRow.value = i;
}

function selectColumn(i) {
  selectedColumn.value = i;
}

function selectCurtain(i) {
  if (isSelectingCurtainStart) {
    selectedCurtainStart.value = i;
    selectedCurtainStop.value = null;
    isSelectingCurtainStart = false;
  }
  else {
    selectedCurtainStop.value = i;
    isSelectingCurtainStart = true;
  }
  instance?.proxy?.$forceUpdate();
}

/*
const curtainStart = computed({
  get() {
    if (!selectedCurtainStart.value) {
      return selectedCurtainStop.value;
    }
    if (!selectedCurtainStop.value) {
      return selectedCurtainStart.value;
    }
    if (selectedCurtainStart.value > selectedCurtainStop.value) {
      return selectedCurtainStop.value;
    }
    return selectedCurtainStart.value;
  }
});
*/

function isCurtainSelected(i) {
  let start = selectedCurtainStart.value;
  let stop = selectedCurtainStop.value;
  if (!start) {
    return false;
  }
  if (!stop) {
    return i == start;
  }
  return (start <= i && i <= stop) || (stop <= i && i <= start);
}

const m280DriveStatus = ref("idle"); //idle, positioning, delivering, storing
const m280GateStatus = ref("empty"); //empty, full
const m280AxisStatus = ref({
  x: { ok: true, enabled: true, running: false, inPosition: false, homingComplete: true, error: false },
  y: { ok: true, enabled: true, running: false, inPosition: false, homingComplete: true, error: false },
  z: { ok: true, enabled: true, running: false, inPosition: false, homingComplete: true, error: false }
});
const m280TrayNumber = ref(47);

function initMQTT() {
  mqtt.ready(() => {
  //mqtt.init(); //Already performed in App.vue
    mqtt.on('connect', () => {
      //responses.value.push({ color: "yellow", topic: "Socket", payload: "connected" });
    });
    mqtt.on('disconnect', () => {
      //responses.value.push({ color: "red", topic: "Socket", payload: "disconnected" });
    });
    mqtt.on('received', (data) => {
      //console.log("received", data);

      try {
        if (data.topic == "hcu/cabinets/" + mac.value + "/debug") {
          //data.payload.type == "io"
          //console.log("debug", data.payload);

          let now = new Date();
          let elapsed = null;

          let type = "warning";
          //let type = "comment";
          if (data.payload.text == "OK" || data.payload.text == "DONE" || data.payload.text == "READY" || data.payload.text == "ACK" || data.payload.text == "REF_OK" || data.payload.text == "RET_OK" || data.payload.text == "FET_OK"
            || /^MOTOR_[ABXYZP]\sOK/g.test(data.payload.text) || data.payload.text == "go to REf") {
            type = "success";
            if (lastCommandTime) {
              elapsed = now.getTime() - lastCommandTime.getTime();
            }
            if (data.payload.text == "DONE" || data.payload.text == "READY") {
              isOpening.value = false;
              canResetLog.value = true;
            }
          }
          else if (/^State\s\d+/g.test(data.payload.text) || /^Motor_[ABXYZP]\sinicializiran/g.test(data.payload.text) || data.payload.text == "PER" || data.payload.text == "END_PER" || data.payload.text == "EVT" || data.payload.text == "END_EVT" || data.payload.text == "Door open") {
            type = "comment";
          }
          else if (data.payload.text == "ERR" || data.payload.text == "ERR_ON_CLOSET" || data.payload.text == "Close and continue") {
            type = "danger";
            if (lastCommandTime) {
              elapsed = now.getTime() - lastCommandTime.getTime();
            }
            isOpening.value = false;
          }
          else if (/^\d+[\s\-;]\d+/g.test(data.payload.text) || /^SET\s\d+/g.test(data.payload.text)) {
            type = "command";
            lastCommandTime = now;
          }
          let time = now.toISOString().split("T")[1].substr(0, 8);
          if (data.payload.text == "PER") {
            insideRecurring = true;
          }
          if (data.payload.text == "END_PER") {
            insideRecurring = false;
          }
          if (data.payload.text && !insideRecurring && data.payload.text != "END_PER") {
            appendLog({
              text: data.payload.text,
              time: time,
              type: type,
              direction: data.payload.dir == "in" ? "inbound" : "outbound",
              elapsed: elapsed ? `+${elapsed}ms` : null,
            });
          }
        }
      }
      catch (err) {
        console.error(err);
      }

    });
  });

  /*
    { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
    { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
    { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
    { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
    { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
    { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
  */
}

function appendLog(record) {
  logRecords.value.push(record);
  if (lastCommandRaw) {
    recentRecords.value.push(record);
  }
}

const choice = ref('protocol');

function setChoice(value) {
  choice.value = value;
}

const recentRecords = ref([
  // { text: "18 23", time: "13:23:00", type: "command", direction: "outbound" },
  // { text: "OK", time: "13:23:00", type: "success", direction: "inbound", elapsed: "+34ms" },
  // { text: "DONE", time: "13:23:10", type: "success", direction: "inbound", elapsed: "+10245ms" },
  // { text: "0, 4; 11", time: "13:23:11", type: "warning", direction: "inbound" },
  // { text: "200 300", time: "13:23:30", type: "command", direction: "outbound" },
  // { text: "ERR", time: "13:23:30", type: "danger", direction: "inbound" },
]);


const command = ref("");

function invokeCommand() {
  if (command.value.trim().length > 0) {
    recentRecords.value = [ ];

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);

    let cmd = command.value;
    if (cmd.toLowerCase().startsWith("set ")) {
      cmd = cmd.toUpperCase();
    }

    cabinet.invokeCommand(cmd);
    
    lastCommandRaw = cmd;
    lastCommandTime = now;

    appendLog({ text: cmd, type: "command", direction: "outbound", time: time });
    command.value = "";
  }
}

function clearRecentLogs() {
  recentRecords.value = [ ];
  command.value = "";
}
</script>

<template>
  <div class="relative">
    <!-- <div><span class="text-lg font-bold dark:text-white">Omara z roleto</span> <span class="text-md text-gray-400 ml-3">150 predalov z vložki</span></div> -->

    <div class="">
      <!-- Top right corner -->
      <div class="flex flex-row justify-between space-x-2 mt-4">

        <div class="relative dark:text-white">
          Miniregalna <span class="font-bold" v-if="props.cabinet && props.cabinet.model">{{ props.cabinet.model.toUpperCase() }}</span>
          <Tag color="blue" class="font-mono ml-2 relative -top-0.5" v-if="mac">{{ mac }}</Tag>
          <Tag color="purple" class="font-mono ml-2 relative -top-0.5" v-if="ip">{{ ip }}</Tag>
        </div>

      </div>
    </div>


    <!-- Top right corner -->
    <div class="flex flex-row justify-items-strech space-x-2 w-full mt-4" v-if="false">

      <!-- Tray -->
      <Label class="label mr-1">Trenutni&nbsp;predal</Label>
      <TextBox v-model="currentTrayNumber" placeholder="--" :readonly="true" style="width: 50px; text-align: center; margin-right: 20px;"></TextBox>

      <!-- Curtain -->
      <Label class="label mr-1">Pozicija&nbsp;zavese</Label>
      <TextBox v-model="currentCurtainPosition" placeholder="--" :readonly="true" style="width: 90px; text-align: center; margin-right: 20px;"></TextBox>

      <!-- Curtain -->
      <!-- <Label class="label mr-1">Stanje pogona</Label>
      <TextBox v-model="currentCurtainPosition" placeholder="idle" :readonly="true" style="width: 90px; text-align: center; margin-right: 20px;"></TextBox> -->

      <!-- Action button -->
      <!-- <Button @click="reset">Reset</Button> -->

    </div>

    <!-- Grid -->
    <div class="w-full mt-4">
      <div class="">

        <!-- Row and column -->
        <!--<div class="" v-if="false">
          <div class="text-md font-bold dark:text-white">Vrsta</div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 10" @click="selectRow(i)" class="button" :class="{ 'button-selected': i == selectedRow }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ alphabet[i - 1] }}</h5>
            </a>
          </div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 4" @click="selectRow(i + 10)" class="button" :class="{ 'button-selected': i + 10 == selectedRow }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ alphabet[i + 10 - 1] }}</h5>
            </a>
          </div>

          <div class="text-md font-bold dark:text-white mt-8">Stolpec</div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 10" @click="selectColumn(i)" class="button" :class="{ 'button-selected': i == selectedColumn }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ i }}</h5>
            </a>
          </div>
          <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
            <a href="#" v-for="i in 1" @click="selectColumn(i + 10)" class="button" :class="{ 'button-selected': i + 10 == selectedColumn }">
              <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{{ i + 10 }}</h5>
            </a>
          </div>
        </div>-->

        <!-- Insert position and length -->
        <div class="text-md font-bold dark:text-white mt-8">{{ $t('service.curtain_position_label') }}</div>
        <div class="flex flex-row gap-2 w-full grid-cols-2 mt-2 mb-2">
          <a href="#" v-for="i in 10" @click="selectCurtain(i)" class="button" :class="{ 'button-selected': isCurtainSelected(i) }">
            <h5 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">V<span class="text-sm">{{ i }}</span></h5>
          </a>
        </div>

        <div class="flex flex-row gap-8">
          <!-- Numpad -->
          <div class="mt-8 w-1/2" style="max-width: 280px;">
            <div class="text-md font-bold dark:text-white">{{ $t('service.tray_select_label') }}</div>
            <KeyboardNumpad v-model="trayNumber" :masked="false" :iconic="false" :readonly="true" :max-length="3" :min-value="1" :max-value="280" @confirm="submit()" />
          </div>

          <!-- Right half -->
          <div class="w-1/2">

            <!-- Commands -->
            <div class="text-md font-bold dark:text-white mt-8">{{ $t('service.commands_label') }}</div>
            <div class="grid gap-2 w-full grid-cols-2 mt-2 mb-2">
              <Button type="primary" left-icon="arrow-up" @click="openTray()">{{ $t('service.tray_open') }}</Button>
              <Button type="primary" left-icon="arrow-down" @click="closeTray()">{{ $t('service.tray_store') }}</Button>
              <Button type="secondary" left-icon="wrench" @click="openTray({ serviceWindow: true })" :title="$t('service.service_window_tooltip')">SET 523</Button>
              <Button type="danger" @click="promptSet800()">SET 800</Button>
              <Button type="success" left-icon="rotate" @click="goToRef()">{{ $t('service.reference') }}</Button>
              <Button type="secondary" left-icon="wrench" @click="set903()" title="Ključavnica (coil key)">SET 903</Button>
            </div>

            <!-- Status and indicators -->
            <div class="mt-8 mb-4">
              <div class="text-md font-bold dark:text-white mb-2">{{ $t('service.status_label') }}</div>
              <div class="grid grid-cols-5 gap-2">
                <span class="tag col-span-3">
                  {{ $t('service.drive_status') }}: <span class="text-blue-500" v-if="movementStatus">{{ movementStatus }}</span><span class="text-blue-500" v-else>--</span>
                </span>
                <span class="tag col-span-2">
                  State: <span class="text-blue-500" v-if="internalState">{{ internalState }}</span><span class="text-blue-500" v-else>--</span>
                </span>
              </div>

              <div class="grid grid-cols-4 gap-2 mt-2">
                <span class="tag tag-success" v-if="isReferenced === true">
                  REF_OK
                </span>
                <span class="tag tag-danger" v-else-if="isReferenced === false">
                  NOT_REF
                </span>
                <span class="tag tag-idle" v-else>
                  REF
                </span>

                <span class="tag" :class="{ 'tag-idle': !isCurtainAlarm, 'tag-danger': isCurtainAlarm }">
                  ROLO
                </span>
                <span class="tag" :class="{ 'tag-idle': !isHaltAlarm, 'tag-danger': isHaltAlarm }">
                  HALT
                </span>
                <span class="tag" :class="{ 'tag-idle': !isDisruptAlarm, 'tag-danger': isDisruptAlarm }">
                  DISRUPT
                </span>
              </div>

              <div class="grid grid-cols-3 gap-2 mt-2" v-if="motorStatus">
                <template v-for="(motor, i) in motors">
                  <span class="tag tag-success" v-if="motorStatus[motor] === 'OK'">
                    MOTOR_{{ motor }}
                  </span>
                  <!-- <span class="tag tag-danger" v-else-if="motorStatus[motor] === '???'">
                    MOTOR_{{ motor }}
                  </span> -->
                  <span class="tag tag-idle" v-else>
                    MOTOR_{{ motor }}
                  </span>
                </template>
              </div>

            </div>
            <!-- End of status -->
            
          </div>
        </div>

      </div>
      
      

      <!-- Axis indicators -->
      <div class="mt-8 mb-4 grid grid-cols-2" v-if="false">
        <div>
          <div class="text-md font-bold dark:text-white mb-2">Indikatorji osi</div>
          <div v-for="axis in ['x','y','z','l','r','p']">
            <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200" v-if="m280AxisStatus && m280AxisStatus[axis]">
              <span class="text-bold mr-2 w-2">{{ axis.toUpperCase() }}</span>
              <i class="fa fa-regular fa-circle-check" :class="{ 'text-green-500': m280AxisStatus[axis].ok, 'text-gray-300': !m280AxisStatus[axis].ok }" title="COM OK"></i>
              <i class="fa fa-power-off" :class="{ 'text-green-500': m280AxisStatus[axis].enabled, 'text-gray-300': !m280AxisStatus[axis].enabled }" title="Enabled"></i>
              <i class="fa fa-spinner" :class="{ 'text-purple-500': m280AxisStatus[axis].running, 'text-gray-300': !m280AxisStatus[axis].running }" title="Running"></i>
              <i class="fa fa-crosshairs" :class="{ 'text-blue-500': m280AxisStatus[axis].inPosition, 'text-gray-300': !m280AxisStatus[axis].inPosition }" title="In position"></i>
              <i class="fa fa-house" :class="{ 'text-blue-500': m280AxisStatus[axis].homingComplete, 'text-gray-300': !m280AxisStatus[axis].homingComplete }" title="Homing complete"></i>
              <i class="fa fa-regular fa-circle-xmark" :class="{ 'text-red-500': m280AxisStatus[axis].error, 'text-gray-300': !m280AxisStatus[axis].error }" title="Error"></i>
            </span>
          </div>
        </div>
        <div>
          <div class="text-md font-bold dark:text-white mb-2">Stanje krmilnika</div>
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Stanje pogona: <span class="text-blue-500">idle|positioning|delivering|storing</span>
          </span><br />
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Stanje izhodnega okna: <span class="text-blue-500">polno|prazno</span>
          </span><br />
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Čaka na ukaz pozicioniranja
          </span><br />
          <span class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            Čaka na ukaz zavese
          </span>
        </div>
      </div>

      <!-- Logger -->
      <!-- <div class="mt-8 pb-4" style="height: 100%;">
        <div class="text-md font-bold dark:text-white mt-8 mb-2">Sporočila</div>
        <EventLog :records="logRecords">
          <template #empty>
            {{ $t('service.no_logs') }}
          </template>
        </EventLog>
      </div> -->

      <!-- Logger -->
      <div class="w-full h-full mt-8">

        <div class="tab-control mb-4">
          <button class="tab-button" :class="{ 'tab-active': choice == 'protocol' }" @click="setChoice('protocol')">{{ $t('service.tab_protocol') }}</button>
          <!-- <button class="tab-button" :class="{ 'tab-active': choice == 'status' }" @click="setChoice('status')">{{ $t('service.tab_status') }}</button> -->
          <button class="tab-button" :class="{ 'tab-active': choice == 'console' }" @click="setChoice('console')">{{ $t('service.tab_console') }}</button>
        </div>

        <EventLog id="eventLog" ref="eventLogElement" :records="logRecords" :style="'height: ' + eventLogHeight + 'px'" v-if="choice == 'protocol'">
          <template #empty>
            {{ $t('service.no_logs') }}
          </template>
        </EventLog>

        <pre class="px-3 py-3 mb-10 border rounded bg-amber-100 text-amber-700 text-xs font-mono" v-if="choice == 'status'">{{ status }}</pre>

        <div class="w-full" v-if="choice == 'console'">
          <div class="flex flex-row gap-2 mb-2">
            <TextBox v-model="command" :placeholder="$t('service.enter_command')" class="w-full" @enter="invokeCommand()" />
            <Button type="secondary" @click="clearRecentLogs()">{{ $t('generic.clear') }}</Button>
            <Button type="primary" @click="invokeCommand()">{{ $t('service.send') }}</Button>
          </div>
          <EventLog :records="recentRecords" :style="'height: ' + (eventLogHeight - 50) + 'px'">
            <template #empty>
              {{ $t('service.no_logs') }}
            </template>
          </EventLog>
        </div>

      </div>
      
    </div>

    <!-- <div class="distancer" style="height: 10px;">
    </div> -->

  </div>
</template>

<style scoped>
.tag {
  @apply inline-flex items-center gap-x-1.5 rounded-md px-2 py-1
    text-xs font-medium ring-1 ring-inset
    text-gray-900 ring-gray-200
    dark:text-white dark:ring-gray-400;
}

.tag.tag-idle {
  @apply text-gray-400 bg-gray-100 ring-gray-200
    dark:text-gray-600 dark:bg-gray-900 dark:ring-gray-600;
}

.tag.tag-success {
  @apply text-green-900 bg-green-100 ring-green-500
    dark:text-green-900 dark:bg-green-400 dark:ring-green-900;
}

.tag.tag-warning {
  @apply text-amber-900 bg-amber-100 ring-amber-500;
}

.tag.tag-danger {
  @apply text-red-900 bg-red-100 ring-red-500
    dark:text-red-200 dark:bg-red-600 dark:ring-red-400;
}

.label {
  position: relative;
  top: 5px;
  height: 30px;
  line-height: 30px;
}

.button {
  @apply flex justify-center place-items-center aspect-square w-16 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.button.button-selected {
  /* border: 2px solid rgba(26,86,219,1); */
  @apply bg-blue-100 border border-blue-500;
}

.button.button-position,
.button.button-position:hover {
  @apply bg-blue-700 text-white;
  color: white !important;
  cursor: default;
}

.badge {
  @apply relative font-mono font-medium mb-1 px-2.5 py-0.5 rounded;
}

.badge.badge-blue {
  @apply bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300;
}

.badge.badge-green {
  @apply bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300;
}

.badge.badge-yellow {
  @apply bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300;
}

.badge.badge-red {
  @apply bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300;
}
</style>
